import { dateUtils } from '@helpers/dateUtils';
import { AuditQueueJob, auditQueueService } from '@services/auditQueue.service';
import { handleError } from '@services/errHandler';
import { globalAuthStore } from '@stores/bootstrap-stores/auth';
import { coreAppStore } from '@stores/utility-stores/project';
import { log } from '@utils/log';
import { shortId } from '@utils/shortid';
import { distinctStrArr } from '@utils/vanillaJs';
import { DocumentScope } from '@vincerosoft/assessfluence-types';
import dayjs from 'dayjs';
import { chunk, cloneDeep, isError } from 'es-toolkit';
import {
  and,
  clearIndexedDbPersistence,
  DocumentData,
  DocumentReference,
  FirestoreDataConverter,
  FirestoreError,
  collection as fsCollection,
  collectionGroup as fsCollectionGroup,
  deleteDoc as fsDeleteDoc,
  doc as fsDoc,
  setDoc as fsSetDoc,
  updateDoc as fsUpdateDoc,
  getCountFromServer,
  getDocFromCache,
  getDocFromServer,
  getDocsFromCache,
  getDocsFromServer,
  PartialWithFieldValue,
  Query,
  query,
  QueryCompositeFilterConstraint,
  QueryConstraint,
  QueryFilterConstraint,
  QueryNonFilterConstraint,
  QuerySnapshot,
  terminate,
  Timestamp,
  UpdateData,
  where,
  WithFieldValue,
  writeBatch
} from 'firebase/firestore';

const isConnectivityError = (error: unknown): boolean => {
  const code = (error as FirestoreError | undefined)?.code;

  return ['unavailable', 'deadline-exceeded', 'cancelled', 'internal'].includes(code || '');
};

const isValidDate = (value: unknown): value is Date =>
  value instanceof Date && !Number.isNaN(value.getTime());

function toSafeDate(value: unknown): Date {
  if (isValidDate(value)) return value;

  const parsed = dateUtils.toDayjs(value as dayjs.ConfigType);
  if (parsed.isValid()) return parsed.toDate();

  return new Date(0); // or throw new Error('Invalid date value')
}

export const convertTimestampToDate = (value: unknown): Date => {
  if (value === null || value === undefined) {
    return new Date(0);
  }

  if (value instanceof Timestamp) {
    return value.toDate();
  }
  const valueAsSeconds = (value as { _seconds?: number | undefined })._seconds;

  if (typeof valueAsSeconds === 'number') {
    return new Date(valueAsSeconds * 1000);
  }

  return toSafeDate(value);
};

type BulkDocInput<T> = {
  id: string;
  data: T;
};

type BulkSetDocInput<T> = BulkDocInput<T> & {
  options?: { merge?: boolean };
};

const queryConfig = <AppModelType, DbModelType extends DocumentData>(
  collRef: Query<AppModelType, DbModelType>,
  queryConstraints: QueryConstraint[],
  compositeFilter?: QueryCompositeFilterConstraint
) => {
  let args: Query<AppModelType, DocumentData> | null = null;

  if (compositeFilter) {
    const { filters, nonFilters } = splitQuery(queryConstraints);

    if (filters?.length) {
      compositeFilter = and(compositeFilter, ...filters.map((f) => f as QueryFilterConstraint));
    }

    args = query(collRef, compositeFilter, ...nonFilters);
    queryConstraints = [];
  } else {
    args = query(collRef, ...queryConstraints);
  }

  return args;
};

const splitQuery = (queryConstraints: QueryConstraint[]) =>
  queryConstraints.reduce(
    (acc, constraint) => {
      if ('type' in constraint) {
        if (
          [
            'orderBy',
            'limit',
            'limitToLast',
            'offset',
            'startAt',
            'startAfter',
            'endAt',
            'endBefore'
          ].includes(constraint.type)
        ) {
          acc.nonFilters.push(constraint as QueryNonFilterConstraint);
        } else {
          acc.filters.push(constraint);
        }
      }
      return acc;
    },
    { filters: [], nonFilters: [] } as {
      filters: QueryConstraint[];
      nonFilters: QueryNonFilterConstraint[];
    }
  );

export type QueryConstraints = QueryConstraint | (QueryConstraint | QueryNonFilterConstraint)[];

export type FsOptions<AppModelType> = {
  isGroupCollection?: boolean;
  dataConverter?: FirestoreDataConverter<AppModelType>;
  compositeFilter?: QueryCompositeFilterConstraint;
  forceServer?: boolean;
};

type AuditLogEntryInput = {
  document: DocumentData | null;
  changes: DocumentData | null;
  type: 'created' | 'updated' | 'deleted' | null;
};

const AUDIT_LOGS_COLLECTION = '_audit-logs';
const AUDIT_TRAILS_COLLECTION = '_audit-trails';

const showToast = (collection: string) => {
  log.info('You are offline and cannot access the ' + collection + ' collection.');

  return true;
};

const converter = <DbModelType extends DocumentData, AppModelType>(): FirestoreDataConverter<
  AppModelType,
  DbModelType
> => ({
  fromFirestore: (snapshot) => {
    const data = snapshot?.data();
    try {
      if (data) {
        if (data.created_at && data.created_at instanceof Timestamp) {
          data.created_at = (data.created_at as Timestamp).toDate();
        }

        if (data.activation_date && data.activation_date instanceof Timestamp) {
          data.activation_date = (data.activation_date as Timestamp).toDate();
        }

        if (data.updated_at && data.updated_at instanceof Timestamp) {
          data.updated_at = (data.updated_at as Timestamp).toDate();
        }

        return data as unknown as AppModelType;
      }
    } catch (_err) {
      const error = new Error('Could not convert ' + snapshot?.id + ' to DbModelType');
      handleError(error, 'Firestore conversion error');
    }
    return {} as AppModelType;
  },
  toFirestore: (
    modelObject: WithFieldValue<
      AppModelType & {
        created_at?: Date;
        updated_at?: Date;
        activation_date?: Date;
        object?: string;
        customer_id?: string;
        entity_id?: string;
      }
    >
  ) => {
    if (modelObject.created_at) {
      if (modelObject.created_at instanceof Date) {
        modelObject.created_at = Timestamp.fromDate(modelObject.created_at);
      } else if (typeof modelObject.created_at === 'string') {
        modelObject.created_at = Timestamp.fromDate(new Date(modelObject.created_at));
      }
    }

    if (modelObject.updated_at) {
      if (modelObject.updated_at instanceof Date) {
        modelObject.updated_at = Timestamp.fromDate(modelObject.updated_at);
      } else if (typeof modelObject.updated_at === 'string') {
        modelObject.updated_at = Timestamp.fromDate(new Date(modelObject.updated_at));
      }
    }

    if (modelObject.activation_date) {
      if (modelObject.activation_date instanceof Date) {
        modelObject.activation_date = Timestamp.fromDate(modelObject.activation_date);
      } else if (typeof modelObject.activation_date === 'string') {
        modelObject.activation_date = Timestamp.fromDate(new Date(modelObject.activation_date));
      }
    }

    if (modelObject.customer_id && !modelObject.entity_id) {
      modelObject.entity_id = modelObject.customer_id;
    }

    if (modelObject.object === 'customer') {
      modelObject.object = 'entity';
    }

    return modelObject as unknown as DbModelType;
  }
});

export class FirestoreService {
  isDisconnected: boolean = typeof navigator !== 'undefined' && !navigator.onLine;

  private offlineEnabled: boolean = false;
  private activeListeners: Array<() => void> = [];
  private docRefCache: Map<string, unknown> = new Map();

  constructor() {
    // Binding methods to ensure 'this' context is maintained when passed as callbacks.

    this.collection = this.collection.bind(this);
    this.collectionGroup = this.collectionGroup.bind(this);
    this.doc = this.doc.bind(this);
    this.addDoc = this.addDoc.bind(this);
    this.addDocs = this.addDocs.bind(this);
    this.setDoc = this.setDoc.bind(this);
    this.setDocs = this.setDocs.bind(this);
    this.updateDoc = this.updateDoc.bind(this);
    this.updateDocs = this.updateDocs.bind(this);
    this.deleteDoc = this.deleteDoc.bind(this);
    this.deleteDocs = this.deleteDocs.bind(this);
    this.getDocsSnapshot = this.getDocsSnapshot.bind(this);
    this.getDocs = this.getDocs.bind(this);
    this.getCount = this.getCount.bind(this);
    this.getDoc = this.getDoc.bind(this);
    this.getDocSnapshot = this.getDocSnapshot.bind(this);
    this.getDocsInBatches = this.getDocsInBatches.bind(this);
    this.getCountInBatches = this.getCountInBatches.bind(this);
    this.firestore = this.firestore.bind(this);

    globalAuthStore.subscribe(
      (state) => state,
      (state) => {
        if (state.customClaims && state.authUser) {
          this.offlineEnabled = !!state.customClaims.offline;
        }
      },
      {
        fireImmediately: true
      }
    );

    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        this.isDisconnected = false;
      });
      window.addEventListener('offline', () => {
        this.isDisconnected = true;
      });
    }
  }

  canContinue() {
    // if the user is offline, can the function continue?
    // user is offline and offline is not enabled
    if (globalAuthStore.getState().authUser && !this.offlineEnabled) return false;

    return true;
  }

  firestore() {
    return coreAppStore.getState().db!;
  }

  private shouldSkipAuditLog(collection: string) {
    return [AUDIT_LOGS_COLLECTION, AUDIT_TRAILS_COLLECTION].includes(collection);
  }

  private getAuditActor() {
    const authUser = globalAuthStore.getState().authUser;
    const providerId = authUser?.providerData?.find((provider) => provider.providerId)?.providerId;

    return {
      uid: authUser?.uid || 'system',
      authType: providerId || (authUser?.isAnonymous ? 'anonymous' : 'app-session')
    };
  }

  private getDocumentScope(document: DocumentData) {
    if (document?.is_tenant) {
      return {
        is_tenant: true,
        domain: 'TENANT'
      } as Partial<DocumentScope>;
    }

    if (document?.group_entity_id) {
      return {
        group_entity_id: document.group_entity_id,
        domain: 'GROUP_ENTITY'
      } as Partial<DocumentScope>;
    }

    if (document?.entity_id || document?.customer_id) {
      return {
        entity_id: document.entity_id || document.customer_id,
        domain: 'ENTITY'
      } as Partial<DocumentScope>;
    }

    if (typeof document?.is_system === 'boolean') {
      return {
        is_system: document.is_system,
        domain: 'SYSTEM'
      } as Partial<DocumentScope>;
    }

    return null;
  }

  private async readDocumentState<DbModelType extends DocumentData, AppModelType = DbModelType>(
    collection: string,
    docId: string
  ): Promise<{
    exists: boolean | null;
    data: DocumentData | null;
  }> {
    try {
      const snapshot = await this.getDocSnapshot<DbModelType, AppModelType>(collection, docId);

      if (!snapshot) {
        return {
          exists: null,
          data: null
        };
      }

      if (!snapshot.exists()) {
        return {
          exists: false,
          data: null
        };
      }

      return {
        exists: true,
        data: cloneDeep(snapshot.data() as DocumentData)
      };
    } catch (error: Error | unknown) {
      const errorMessage = isError(error) ? error.message : String(error);
      log.warn(`Skipping audit snapshot read for ${collection}/${docId}: ${errorMessage}`);

      return {
        exists: null,
        data: null
      };
    }
  }

  private async queueAuditLog(collection: string, docId: string, auditEntry: AuditLogEntryInput) {
    log.info(`Queueing audit log for ${collection}/${docId}`);
    if (this.shouldSkipAuditLog(collection)) {
      return;
    }

    const { document, changes, type } = auditEntry;

    if (!document || !changes || !type) {
      return;
    }

    const project = coreAppStore.getState().project;

    if (!project?.tenant_id || !project?.project_id) {
      log.warn(`Skipping audit queue for ${collection}/${docId}: missing tenant context`);
      return;
    }

    const { uid, authType } = this.getAuditActor();
    const timestamp = new Date().toISOString();
    const job: AuditQueueJob = {
      jobId: `audit_${shortId(16)}`,
      tenantId: project.tenant_id,
      databaseId: project.project_id,
      collection,
      docId,
      operation: type,
      actor: {
        uid,
        authType
      },
      documentScope: this.getDocumentScope(document) as DocumentScope | null,
      resourceType: (document.object as string) || collection,
      resourceOwnedBy: document.owned_by,
      changes: cloneDeep(changes) as Record<string, unknown>,
      createdAt: timestamp
    };

    try {
      await auditQueueService.enqueueJob(job);
    } catch (error: Error | unknown) {
      const errorMessage = isError(error) ? error.message : String(error);
      log.warn(`Failed to enqueue audit log for ${collection}/${docId}: ${errorMessage}`);
    }
  }

  private async commitBatchedWrites<T>(
    items: T[],
    commitChunk: (batchItems: T[]) => Promise<void>
  ) {
    const itemChunks = chunk(items, 500);

    for (const itemChunk of itemChunks) {
      await commitChunk(itemChunk);
    }
  }

  getBatch() {
    return writeBatch(this.firestore());
  }

  registerListener(removeListener: () => void) {
    this.activeListeners.push(removeListener);
    return removeListener;
  }

  // Method to unsubscribe all listeners
  unsubscribeAllListeners() {
    try {
      const totalListeners = this.activeListeners?.length;
      log.info(`Unsubscribing from ${totalListeners} active Firestore listeners`);

      if (totalListeners === 0) {
        return;
      }

      // Safety mechanism to prevent infinite loop
      const maxIterations = totalListeners + 10; // Allow some buffer
      let iterations = 0;

      while (this.activeListeners.length > 0) {
        // Check for potential infinite loop
        iterations++;
        if (iterations > maxIterations) {
          const remainingCount = this.activeListeners.length;
          log.error(
            `Possible infinite loop detected while unsubscribing. Forcing cleanup of ${remainingCount} remaining listeners.`
          );
          this.activeListeners = []; // Force clear
          break;
        }

        const lastActiveListener = this.activeListeners.pop();
        if (typeof lastActiveListener === 'function') {
          try {
            lastActiveListener();
          } catch (err) {
            const errorMessage = isError(err) ? err.message : String(err);
            log.warn(`Failed to unsubscribe from Firestore listener: ${errorMessage}`);
          }
        }
      }

      log.info(`Subscription process completed. Processed ${iterations} listeners`);
    } catch (error) {
      const errorMessage = isError(error) ? error.message : String(error);
      log.error(`Critical error during Firebase listener cleanup: ${errorMessage}`);
      // Emergency cleanup
      this.activeListeners = [];
    }
  }

  async clearPersistence() {
    try {
      // First terminate any existing connections
      await terminate(this.firestore());

      // Add a small delay to ensure termination completes
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Then attempt to clear persistence
      await clearIndexedDbPersistence(this.firestore());

      return true;
    } catch (error) {
      if (error instanceof FirestoreError && error.code === 'failed-precondition') {
        // Try again with a different approach - notify the user to close other tabs
        log.error('Please close all other browser tabs of this application and try again.');
      } else {
        const errorMessage = isError(error) ? error.message : String(error);
        log.error(`Error clearing persistence: ${errorMessage}`);
      }
      throw error;
    }
  }

  collection<DbModelType extends DocumentData, AppModelType = DbModelType>(
    path: string,
    dataConverter: FirestoreDataConverter<AppModelType, DbModelType> = converter()
  ) {
    return fsCollection(this.firestore(), path).withConverter(dataConverter);
  }

  subCollection<DbModelType extends DocumentData, AppModelType = DbModelType>(
    parentPath: string,
    parentDocId: string,
    subCollectionName: string,
    dataConverter: FirestoreDataConverter<AppModelType, DbModelType> = converter()
  ) {
    const fullPath = `${parentPath}/${parentDocId}/${subCollectionName}`;
    return fsCollection(this.firestore(), fullPath).withConverter(dataConverter);
  }

  collectionGroup<DbModelType extends DocumentData, AppModelType = DbModelType>(
    path: string,
    dataConverter: FirestoreDataConverter<AppModelType, DbModelType> = converter()
  ) {
    return fsCollectionGroup(this.firestore(), path).withConverter(dataConverter);
  }

  doc<DbModelType extends DocumentData, AppModelType = DbModelType>(
    path: string,
    docId: string,
    dataConverter: FirestoreDataConverter<AppModelType, DbModelType> = converter()
  ) {
    if (!path || !docId) {
      log.info('Invalid path or docId:', path, docId);
      throw new Error('Invalid path or docId');
    }

    const cacheKey = `${path}/${docId}`;

    if (!this.docRefCache.has(cacheKey)) {
      const docRef = fsDoc(this.firestore(), path, docId).withConverter(dataConverter);
      this.docRefCache.set(cacheKey, docRef);
    }

    return this.docRefCache.get(cacheKey) as DocumentReference<AppModelType, DbModelType>;
  }

  async addDoc<DbModelType extends DocumentData, AppModelType = DbModelType>(
    collection: string,
    docId: string,
    data: WithFieldValue<AppModelType>
  ) {
    const docRef = this.doc<DbModelType, AppModelType>(collection, docId);

    (
      data as {
        created_at: Date;
      }
    ).created_at = new Date();
    (
      data as {
        updated_at: Date;
      }
    ).updated_at = new Date();

    try {
      if (this.isDisconnected) {
        // In offline mode, use Promise.race with a timeout to prevent hanging
        await Promise.race([
          fsSetDoc<AppModelType, DbModelType>(docRef, data),
          new Promise((resolve) => {
            setTimeout(() => {
              log.info('Operation queued for offline sync');
              resolve(null);
            }, 1000);
          })
        ]);
      } else {
        await fsSetDoc<AppModelType, DbModelType>(docRef, data);
      }
    } catch (error) {
      const errorMessage = isError(error) ? error.message : String(error);
      log.error(`Error adding document: ${errorMessage}`);
      // Don't rethrow in offline mode to allow the function to continue
      if (!this.isDisconnected) {
        throw error;
      }
    }

    const auditData = cloneDeep(data as DocumentData);

    await this.queueAuditLog(collection, docId, {
      document: auditData,
      changes: cloneDeep(auditData),
      type: 'created'
    });

    return {
      id: docId,
      created_at: new Date()
    };
  }

  async addDocs<DbModelType extends DocumentData, AppModelType extends DocumentData = DbModelType>(
    collection: string,
    docs: BulkDocInput<WithFieldValue<AppModelType>>[]
  ) {
    if (!docs?.length) {
      return [];
    }

    const timestamp = new Date();

    await this.commitBatchedWrites(docs, async (docChunk) => {
      const batch = this.getBatch();

      docChunk.forEach(({ id, data }) => {
        const docRef = this.doc<DbModelType, AppModelType>(collection, id);

        (
          data as {
            created_at: Date;
          }
        ).created_at = new Date(timestamp);
        (
          data as {
            updated_at: Date;
          }
        ).updated_at = new Date(timestamp);

        batch.set(docRef, data);
      });

      await batch.commit();
    });

    await Promise.all(
      docs.map(async ({ id, data }) => {
        const auditData = cloneDeep(data as DocumentData);

        await this.queueAuditLog(collection, id, {
          document: auditData,
          changes: cloneDeep(auditData),
          type: 'created'
        });
      })
    );

    return docs.map(({ id }) => ({
      id,
      created_at: new Date(timestamp)
    }));
  }

  async setDoc<DbModelType extends DocumentData, AppModelType = DbModelType>(
    collection: string,
    docId: string,
    data: PartialWithFieldValue<AppModelType>,
    options?: { merge?: boolean }
  ) {
    options = {
      merge: true,
      ...(options || {})
    };
    const docRef = this.doc<DbModelType, AppModelType>(collection, docId);
    if (
      !(
        data as unknown as {
          created_at: Date;
        }
      ).created_at
    ) {
      (
        data as unknown as {
          created_at: Date;
        }
      ).created_at = new Date();
    }

    (
      data as unknown as {
        updated_at: Date;
      }
    ).updated_at = new Date();

    try {
      if (this.isDisconnected) {
        // In offline mode, use Promise.race with a timeout to prevent hanging
        await Promise.race([
          fsSetDoc<AppModelType, DbModelType>(docRef, data, {
            merge: options.merge
          }),
          new Promise((resolve) => {
            setTimeout(() => {
              log.info('Operation queued for offline sync');
              resolve(null);
            }, 1000);
          })
        ]);
      } else {
        await fsSetDoc<AppModelType, DbModelType>(docRef, data, {
          merge: options.merge
        });
      }
    } catch (error) {
      log.error('Error setting document:', error);
      // Don't rethrow in offline mode to allow the function to continue
      if (!this.isDisconnected) {
        throw error;
      }
    }

    await this.queueAuditLog(collection, docId, {
      document: cloneDeep(data as DocumentData),
      changes: cloneDeep(data as DocumentData),
      type: options.merge ? 'updated' : 'created'
    });

    return {
      id: docId,
      updated_at: new Date()
    };
  }

  async setDocs<DbModelType extends DocumentData, AppModelType extends DocumentData = DbModelType>(
    collection: string,
    docs: BulkSetDocInput<PartialWithFieldValue<AppModelType>>[]
  ) {
    if (!docs?.length) {
      return [];
    }

    const timestamp = new Date();

    await this.commitBatchedWrites(docs, async (docChunk) => {
      const batch = this.getBatch();

      docChunk.forEach(({ id, data, options }) => {
        const docRef = this.doc<DbModelType, AppModelType>(collection, id);
        const mergeOptions = {
          merge: true,
          ...(options || {})
        };

        if (
          !(
            data as unknown as {
              created_at: Date;
            }
          ).created_at
        ) {
          (
            data as unknown as {
              created_at: Date;
            }
          ).created_at = new Date(timestamp);
        }

        (
          data as unknown as {
            updated_at: Date;
          }
        ).updated_at = new Date(timestamp);

        batch.set(docRef, data, {
          merge: mergeOptions.merge
        });
      });

      await batch.commit();
    });

    await Promise.all(
      docs.map(async ({ id, data, options }) => {
        await this.queueAuditLog(collection, id, {
          document: cloneDeep(data as DocumentData),
          changes: cloneDeep(data as DocumentData),
          type: options?.merge === false ? 'created' : 'updated'
        });
      })
    );

    return docs.map(({ id }) => ({
      id,
      updated_at: new Date(timestamp)
    }));
  }

  async updateDoc<
    DbModelType extends DocumentData,
    AppModelType extends DocumentData = DbModelType
  >(collection: string, docId: string, data: UpdateData<AppModelType>) {
    (
      data as unknown as {
        updated_at: Date;
      }
    ).updated_at = new Date();

    const docRef = this.doc<AppModelType, DbModelType>(collection, docId);

    try {
      if (this.isDisconnected) {
        // In offline mode, use Promise.race with a timeout to prevent hanging
        await Promise.race([
          fsUpdateDoc(docRef, data),
          new Promise((resolve) => {
            setTimeout(() => {
              log.info('Operation queued for offline sync');
              resolve(null);
            }, 1000);
          })
        ]);
      } else {
        await fsUpdateDoc(docRef, data);
      }
    } catch (error) {
      log.error('Error updating document:', error);
      // Don't rethrow in offline mode to allow the function to continue
      if (!this.isDisconnected) {
        throw error;
      }
    }

    await this.queueAuditLog(collection, docId, {
      document: cloneDeep(data as DocumentData),
      changes: cloneDeep(data as DocumentData),
      type: 'updated'
    });

    return {
      id: docId,
      updated_at: new Date()
    };
  }

  async updateDocs<
    DbModelType extends DocumentData,
    AppModelType extends DocumentData = DbModelType
  >(collection: string, docs: BulkDocInput<UpdateData<AppModelType>>[]) {
    if (!docs?.length) {
      return [];
    }

    const timestamp = new Date();

    await this.commitBatchedWrites(docs, async (docChunk) => {
      const batch = this.getBatch();

      docChunk.forEach(({ id, data }) => {
        const docRef = this.doc<AppModelType, DbModelType>(collection, id);

        if (
          !(
            data as unknown as {
              created_at: Date;
            }
          ).created_at
        ) {
          (
            data as unknown as {
              created_at: Date;
            }
          ).created_at = new Date(timestamp);
        }

        (
          data as unknown as {
            updated_at: Date;
          }
        ).updated_at = new Date(timestamp);

        batch.update(docRef, data);
      });

      await batch.commit();
    });

    await Promise.all(
      docs.map(async ({ id, data }) => {
        await this.queueAuditLog(collection, id, {
          document: cloneDeep(data as DocumentData),
          changes: cloneDeep(data as DocumentData),
          type: 'updated'
        });
      })
    );

    return docs.map(({ id }) => ({
      id,
      updated_at: new Date(timestamp)
    }));
  }

  async deleteDoc<DbModelType extends DocumentData, AppModelType = DbModelType>(
    collection: string,
    docId: string
  ) {
    const beforeState = await this.readDocumentState<DbModelType, AppModelType>(collection, docId);

    const docRef = this.doc<DbModelType, AppModelType>(collection, docId);

    try {
      if (this.isDisconnected) {
        // In offline mode, use Promise.race with a timeout to prevent hanging
        await Promise.race([
          fsDeleteDoc<AppModelType, DbModelType>(docRef),
          new Promise((resolve) => {
            setTimeout(() => {
              log.info('Operation queued for offline sync');
              resolve(null);
            }, 1000);
          })
        ]);
      } else {
        await fsDeleteDoc<AppModelType, DbModelType>(docRef);
      }
    } catch (error) {
      log.error('Error deleting document:', error);
      // Don't rethrow in offline mode to allow the function to continue
      if (!this.isDisconnected) {
        throw error;
      }
    }

    await this.queueAuditLog(collection, docId, {
      document: beforeState.data,
      changes: beforeState.data,
      type: beforeState.data ? 'deleted' : null
    });
  }

  async deleteDocs<DbModelType extends DocumentData, AppModelType = DbModelType>(
    collection: string,
    docIds: string[]
  ) {
    if (!docIds?.length) {
      return;
    }

    const beforeStates = await Promise.all(
      docIds.map(async (docId) => ({
        docId,
        beforeState: await this.readDocumentState<DbModelType, AppModelType>(collection, docId)
      }))
    );

    await this.commitBatchedWrites(docIds, async (docIdChunk) => {
      const batch = this.getBatch();

      docIdChunk.forEach((docId) => {
        const docRef = this.doc<DbModelType, AppModelType>(collection, docId);
        batch.delete(docRef);
      });

      await batch.commit();
    });

    await Promise.all(
      beforeStates.map(async ({ docId, beforeState }) => {
        await this.queueAuditLog(collection, docId, {
          document: beforeState.data,
          changes: beforeState.data,
          type: beforeState.data ? 'deleted' : null
        });
      })
    );
  }

  async getDocsSnapshot<
    DbModelType extends DocumentData,
    AppModelType extends DocumentData = DbModelType
  >(
    collection: string,
    queryConstraints: QueryConstraints | null = null,
    options?: FsOptions<AppModelType>
  ) {
    let snapshot: QuerySnapshot<AppModelType, DocumentData> | null = null;
    try {
      if (!queryConstraints) {
        queryConstraints = [];
      } else if (!Array.isArray(queryConstraints)) {
        const firestoreQuery = queryConstraints as QueryConstraint;
        queryConstraints = [firestoreQuery];
      }

      const sanitizedConstraints = queryConstraints.filter(
        (constraint): constraint is QueryConstraint | QueryNonFilterConstraint =>
          constraint !== null && constraint !== undefined
      );
      if (sanitizedConstraints.length !== queryConstraints.length) {
        log.error(
          `Removed ${queryConstraints.length - sanitizedConstraints.length} null/undefined query constraint(s) for collection "${collection}". ` +
            'Check the call site for a conditional constraint expression that can resolve to null/undefined.'
        );
      }
      queryConstraints = sanitizedConstraints;

      if (this.isDisconnected && !this.canContinue()) {
        showToast(collection);
        return;
      }

      const collRef = options?.isGroupCollection ? this.collectionGroup : this.collection;

      const args = queryConfig(
        collRef(collection, options?.dataConverter),
        queryConstraints,
        options?.compositeFilter
      );

      log.info('getDocsSnapshot for ' + collection, queryConstraints);

      const readFromCache = this.isDisconnected && !options?.forceServer;

      snapshot = (await (readFromCache
        ? getDocsFromCache(args)
        : getDocsFromServer(args))) as QuerySnapshot<AppModelType, DocumentData>;

      if (!readFromCache) {
        this.isDisconnected = false;
      }

      if (!snapshot) {
        // in case of a false connect status
        const auth = globalAuthStore.getState().customClaims;

        if (auth?.offline && !this.isDisconnected) {
          snapshot = (await getDocsFromCache(args)) as QuerySnapshot<AppModelType, DocumentData>;
        } else {
          // If no snapshot is found, try to get the snapshot from the server
          snapshot = (await getDocsFromServer(args)) as QuerySnapshot<AppModelType, DocumentData>;
        }
        setTimeout(() => {
          showToast(collection);
        }, 3000);
      }
    } catch (error) {
      if (isConnectivityError(error)) {
        this.isDisconnected = true;
      }

      const errorMessage = isError(error) ? error.message : String(error);
      throw Error(
        'Failed to get docs from collection ' + collection + ' with error: ' + errorMessage
      );
    }
    return snapshot;
  }

  async getDocs<DbModelType extends DocumentData, AppModelType extends DocumentData = DbModelType>(
    collection: string,
    queryConstraints?: QueryConstraints,
    options?: FsOptions<AppModelType>
  ) {
    // getDocsSnapshot already wraps/rethrows errors with the collection context,
    // so no extra try/catch here to avoid double-wrapping the message.
    const snapshot = await this.getDocsSnapshot<DbModelType, AppModelType>(
      collection,
      queryConstraints,
      options
    );

    if (!snapshot || snapshot?.empty) {
      return [] as AppModelType[];
    }

    return snapshot.docs.map((doc) => doc.data());
  }

  async getCount<DbModelType, AppModelType = DbModelType>(
    collection: string,
    queryConstraints: QueryConstraints | QueryNonFilterConstraint = [],
    options?: FsOptions<AppModelType>
  ) {
    if (!Array.isArray(queryConstraints)) {
      queryConstraints = [queryConstraints];
    }

    // See getDocsSnapshot: strip out null/undefined constraints from conditional expressions.
    queryConstraints = queryConstraints.filter(
      (constraint) => constraint !== null && constraint !== undefined
    );

    const collRef = options?.isGroupCollection ? this.collectionGroup : this.collection;

    const args = queryConfig(
      collRef(collection, options?.dataConverter),
      queryConstraints,
      options?.compositeFilter
    );

    if (this.isDisconnected && !this.canContinue()) {
      showToast(collection);
      return;
    }

    if (this.isDisconnected) {
      return (await getDocsFromCache(args)).size;
    }

    return (await getCountFromServer(args)).data().count;
  }

  async getDoc<DbModelType extends DocumentData, AppModelType = DbModelType>(
    collection: string,
    docId: string,
    options?: {
      forceServer?: boolean;
      dataConverter?: FirestoreDataConverter<AppModelType, DbModelType>;
    }
  ) {
    const snapshot = await this.getDocSnapshot<DbModelType, AppModelType>(
      collection,
      docId,
      options
    );

    if (!snapshot || !snapshot.exists()) {
      throw Error('Document not found for ' + docId + ' in the ' + collection + ' collection.');
    }

    return snapshot.data() as AppModelType;
  }

  async getDocSnapshot<DbModelType extends DocumentData, AppModelType = DbModelType>(
    collection: string,
    docId: string,
    options?: {
      forceServer?: boolean;
      dataConverter?: FirestoreDataConverter<AppModelType, DbModelType>;
    }
  ) {
    log.info('getDocSnapshot for ' + collection, docId);

    try {
      const docRef = this.doc<DbModelType, AppModelType>(collection, docId, options?.dataConverter);

      if (this.isDisconnected && !this.canContinue()) {
        showToast(collection);
        return;
      }

      const readFromCache = this.isDisconnected && !options?.forceServer;

      let snapshot = await (readFromCache ? getDocFromCache(docRef) : getDocFromServer(docRef));

      if (!snapshot) {
        snapshot = await getDocFromServer(docRef);
      }

      return snapshot;
    } catch (error) {
      const errorMessage = isError(error) ? error.message : String(error);

      throw Error(
        'Failed to get doc ' +
          docId +
          ' from collection ' +
          collection +
          ' with error: ' +
          errorMessage
      );
    }
  }

  async getDocsInBatches<
    DbModelType extends DocumentData,
    AppModelType extends DocumentData = DbModelType
  >(
    collection: string,
    [fieldPath, opStr, value]: [string, 'in' | 'array-contains-any', string[]],
    queryConstraints: QueryConstraints = [],
    updateStoreData?: (data: AppModelType[]) => void,
    options?: FsOptions<AppModelType> & {
      batchSize?: number;
    }
  ) {
    try {
      const uniqueValues = distinctStrArr(value);
      if (!uniqueValues || uniqueValues.length === 0) {
        return [];
      }

      if (!Array.isArray(queryConstraints)) {
        queryConstraints = [queryConstraints];
      }

      let compoundedData: AppModelType[] = [];
      const dataArr: AppModelType[][] = [];

      const processSnapshot = (snapshot: QuerySnapshot<AppModelType>) => {
        const data = cloneDeep(
          !snapshot || snapshot?.empty ? [] : snapshot.docs.map((doc) => doc.data())
        );

        // Add to accumulated data
        compoundedData = [...compoundedData, ...data];
        dataArr.push(data);

        // Update the store with current accumulated data
        if (updateStoreData && typeof updateStoreData === 'function') {
          updateStoreData(compoundedData);
        }
      };
      const chunks = chunk(uniqueValues, options?.batchSize || 24);
      for (const valueBatch of chunks) {
        try {
          const snapshot = await this.getDocsSnapshot<DbModelType, AppModelType>(
            collection,
            [where(fieldPath, opStr, valueBatch), ...(queryConstraints as QueryConstraint[])],
            options
          );
          if (!snapshot) {
            continue;
          }

          processSnapshot(snapshot);
        } catch (error) {
          log.error(`Error fetching batch from ${collection}:`, error);
        }
      }

      return dataArr;
    } catch (err) {
      const errorMessage = isError(err) ? err.message : String(err);
      throw Error(
        'Failed to get docs from collection ' + collection + ' with error: ' + errorMessage
      );
    }
  }

  async getCountInBatches<DbModelType, AppModelType = DbModelType>(
    collection: string,
    [field, opStr, values]: [string, 'in' | 'array-contains-any', string[]],
    queryConstraints: QueryConstraint | QueryConstraint[] = [],
    options?: FsOptions<AppModelType>
  ) {
    try {
      if (!values || values.length === 0) {
        return 0;
      }

      const chunks = chunk(values, 25);

      const promises = chunks.map((dataChunk) => {
        return this.getCount<DbModelType, AppModelType>(
          collection,
          [
            where(field, opStr, dataChunk),
            ...(Array.isArray(queryConstraints) ? queryConstraints : [queryConstraints])
          ],
          options
        );
      });

      const data = await Promise.all(promises);
      return data.reduce((acc, val) => (acc ?? 0) + (val ?? 0), 0);
    } catch (error) {
      const errorMessage = isError(error) ? error.message : String(error);
      throw Error(
        'Failed to get docs from collection ' + collection + ' with error: ' + errorMessage
      );
    }
    // Extract the Firebase index creation URL from the error message
    /*  const urlMatch = err.message?.match(
        /(https:\/\/console\.firebase\.google\.com[^\s]+)/
      );
      const indexUrl = urlMatch ? urlMatch[1] : null;

      let errorMessage =
        'Failed to count docs from collection ' +
        collection +
        ' with error: ' +
        err.message;

      if (indexUrl) {
        errorMessage += '\n\nCreate the required index here: ' + indexUrl;
        log.error('Firebase Index Required:', indexUrl);
      }

      throw Error(errorMessage); */
  }
}

export const fsService = new FirestoreService();
